import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LucideIcon from "../../../components/LucideIcon";
import axios from "axios";
import LoadingButton from "../../../components/LoadingButton";
import SmartListPicker from "../../../components/SmartListPicker";
import FieldMappingSelector from "../../../components/FieldMappingSelector";
import SendScheduleEditor, {
  ScheduleSettings,
  buildDefaultScheduleSettings,
} from "../../../components/SendScheduleEditor";
import { hostname, parseError } from "../../../utils";
import { loadAllSenderProfiles } from "../../../components/SearchableSenderProfileSelect";
import { EmailBuilderTemplate } from "../../../components/EmailBuilder";
import VisualEmailBuilderDialog from "./VisualEmailBuilderDialog";

export interface CampaignTemplate {
  id: string;
  companyId: string;
  uid: string;
  name: string;
  description?: string | null;
  subject: string;
  replyTo?: string | null;
  bodyHtml: string;
  bodyDocument?: EmailBuilderTemplate | null;
  senderProfileId?: string | null;
  listId?: string | null;
  fieldMappings?: { email?: string[] } & Record<string, string[] | undefined>;
  scheduleSettings?: ScheduleSettings | null;
  chatHistory?: ChatMessage[];
  smartList?: { id: string; name: string };
  senderProfile?: { id: string; name?: string; email?: string; integrationId?: string | null };
  created_date?: string;
  updated_date?: string;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  timestamp?: string;
}

interface SenderProfileOption {
  id: string;
  name: string;
  email: string;
  integrationId?: string | null;
}

interface ListHeaderOption {
  id: string;
  name: string;
  type?: string;
}

interface TemplateBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (template: CampaignTemplate) => void;
  onError: (err: string) => void;
  companyId: string;
  getToken: () => Promise<string>;
  template?: CampaignTemplate | null;
}

const TemplateBuilderDialog: React.FC<TemplateBuilderDialogProps> = ({
  open,
  onClose,
  onSaved,
  onError,
  companyId,
  getToken,
  template,
}) => {
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [subject, setSubject] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [replyToError, setReplyToError] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [bodyDocument, setBodyDocument] = useState<EmailBuilderTemplate | null>(null);
  const [senderProfileId, setSenderProfileId] = useState("");
  const [listId, setListId] = useState("");
  const [fieldMappings, setFieldMappings] = useState<{ email?: string[] }>({});
  const [scheduleSettings, setScheduleSettings] = useState<ScheduleSettings>(
    buildDefaultScheduleSettings()
  );
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [visualDialogOpen, setVisualDialogOpen] = useState(false);

  const [senderProfiles, setSenderProfiles] = useState<SenderProfileOption[]>([]);
  const [listHeaders, setListHeaders] = useState<ListHeaderOption[]>([]);

  useEffect(() => {
    if (!open) return;
    setTemplateId(template?.id || null);
    setName(template?.name || `Untitled ${new Date().toLocaleDateString()}`);
    setDescription(template?.description || "");
    setSubject(template?.subject || "");
    setReplyTo(template?.replyTo || "");
    setReplyToError("");
    setBodyHtml(template?.bodyHtml || "");
    setBodyDocument(template?.bodyDocument || null);
    setSenderProfileId(template?.senderProfileId || "");
    setListId(template?.listId || "");
    setFieldMappings((template?.fieldMappings as any) || {});
    setScheduleSettings(
      (template?.scheduleSettings as ScheduleSettings) ||
        buildDefaultScheduleSettings()
    );
    setChat(template?.chatHistory || []);
  }, [open, template]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const token = await getToken();
        const profiles = await loadAllSenderProfiles(token, companyId);
        setSenderProfiles(profiles as SenderProfileOption[]);
      } catch (err) {
        console.error("Failed to load sender profiles", err);
      }
    })();
  }, [open, companyId, getToken]);

  useEffect(() => {
    if (!listId) {
      setListHeaders([]);
      return;
    }
    (async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${hostname}/lists/${listId}`, {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        });
        const listData = res.data?.list || res.data;
        const headers = (listData?.headers || []).filter(
          (h: any) => h.type !== "action"
        );
        setListHeaders(headers);
      } catch (err) {
        console.error("Failed to load list headers", err);
      }
    })();
  }, [listId, companyId, getToken]);

  const validateReplyTo = (): boolean => {
    const trimmed = replyTo.trim();
    if (!trimmed) {
      setReplyToError("");
      return true;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setReplyToError("Enter a valid email address");
      return false;
    }
    setReplyToError("");
    return true;
  };

  const buildPayload = () => ({
    companyId,
    name,
    description: description || null,
    subject,
    replyTo: replyTo.trim() || null,
    bodyHtml,
    bodyDocument,
    senderProfileId: senderProfileId || null,
    listId: listId || null,
    fieldMappings,
    scheduleSettings,
    chatHistory: chat,
  });

  const ensureSaved = useCallback(async (): Promise<CampaignTemplate | null> => {
    const token = await getToken();
    const payload = buildPayload();
    if (templateId) {
      const res = await axios.patch(
        `${hostname}/marketing-campaign-templates/${templateId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data;
    }
    const res = await axios.post(
      `${hostname}/marketing-campaign-templates`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setTemplateId(res.data?.id || null);
    return res.data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, getToken, companyId, name, description, subject, replyTo, bodyHtml, bodyDocument, senderProfileId, listId, fieldMappings, chat]);

  const handleSave = async () => {
    if (!name.trim()) {
      onError("Template name is required.");
      return;
    }
    if (!validateReplyTo()) return;
    setSaving(true);
    try {
      const saved = await ensureSaved();
      if (saved) onSaved(saved);
    } catch (err) {
      onError(parseError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenVisualBuilder = async () => {
    if (!validateReplyTo()) return;
    try {
      const saved = await ensureSaved();
      if (!saved) return;
      setVisualDialogOpen(true);
    } catch (err) {
      onError(parseError(err));
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {templateId ? "Edit Template" : "New Marketing Campaign Template"}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <LucideIcon name="X" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Template Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
          />
          <TextField
            label="Default Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            helperText="Can also use variables like {{firstName}}"
          />
          <TextField
            label="Reply-To Email (optional)"
            type="email"
            value={replyTo}
            onChange={(e) => {
              setReplyTo(e.target.value);
              if (replyToError) setReplyToError("");
            }}
            onBlur={validateReplyTo}
            error={!!replyToError}
            helperText={
              replyToError ||
              "Replies will go here instead of the sender profile's address."
            }
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Sender Profile</InputLabel>
            <Select
              label="Sender Profile"
              value={senderProfileId}
              onChange={(e) => setSenderProfileId(e.target.value as string)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {senderProfiles.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name ? `${p.name} <${p.email}>` : p.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <SmartListPicker
            companyId={companyId}
            getToken={getToken}
            value={listId}
            onChange={setListId}
            label="Smart List (optional)"
            showCount
          />

          {listHeaders.length > 0 && (
            <Accordion disableGutters variant="outlined">
              <AccordionSummary expandIcon={<LucideIcon name="ChevronDown" />}>
                <Typography variant="subtitle2">Email field mapping</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FieldMappingSelector
                  fieldMappings={fieldMappings}
                  onChange={(m) => setFieldMappings(m)}
                  availableHeaders={listHeaders}
                  fieldTypes={["email"]}
                  showHelp={false}
                />
              </AccordionDetails>
            </Accordion>
          )}

          <Accordion disableGutters variant="outlined">
            <AccordionSummary expandIcon={<LucideIcon name="ChevronDown" />}>
              <Typography variant="subtitle2">Send Schedule</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SendScheduleEditor
                value={scheduleSettings}
                onChange={setScheduleSettings}
                helperText="Campaigns created from this template inherit this schedule by default."
              />
            </AccordionDetails>
          </Accordion>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<LucideIcon name="PenTool" size={18} />}
              onClick={handleOpenVisualBuilder}
            >
              Visual Builder
            </Button>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Preview
            </Typography>
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
                backgroundColor: "action.hover",
                height: 480,
              }}
            >
              {bodyHtml ? (
                <iframe
                  title="Template preview"
                  srcDoc={bodyHtml}
                  sandbox=""
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    backgroundColor: "#fff",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 3,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" align="center">
                    No content yet — open the Visual Builder to design this template.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          loading={saving}
          onClick={handleSave}
          variant="contained"
          disabled={!name.trim()}
        >
          Save Template
        </LoadingButton>
      </DialogActions>
    </Dialog>
    <VisualEmailBuilderDialog
      open={visualDialogOpen}
      onClose={() => setVisualDialogOpen(false)}
      companyId={companyId}
      getToken={getToken}
      name={name}
      initialBodyDocument={bodyDocument}
      listHeaders={listHeaders}
      onSave={async (html, document) => {
        // Templates persist immediately on visual save (campaigns batch the save
        // into the parent form). handleOpenVisualBuilder guarantees templateId.
        if (!templateId) {
          onError("Save the template basics before opening the visual builder.");
          return;
        }
        const token = await getToken();
        const res = await axios.patch(
          `${hostname}/marketing-campaign-templates/${templateId}`,
          { companyId, bodyHtml: html, bodyDocument: document },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const updated: CampaignTemplate = res.data;
        setBodyHtml(updated.bodyHtml || "");
        setBodyDocument(updated.bodyDocument || null);
      }}
      onError={onError}
    />
    </>
  );
};

export default TemplateBuilderDialog;
